import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRightIcon, ClockIcon, MapPinIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { business, retiredShopPaths, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';

/**
 * Landing page for former online-shop categories. Instead of a product grid,
 * it points the visitor to the showroom by phone, text or a visit.
 */
export function ShowroomInquiryPage() {
  const {
    pathname
  } = useLocation();
  const match = retiredShopPaths.find(item => item.path === pathname);
  const subject = match ? match.label : 'this item';
  useEffect(() => {
    document.title = 'Ask Us in the Showroom | Pegasus Spas';
    window.scrollTo(0, 0);
  }, []);
  return <main data-id="element-2028" data-mp="3rqcs4w0iorkh">
      <section aria-labelledby="inquiry-h1" className="w-full bg-water" data-id="element-2029" data-mp="3rqmbi0r240h7">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2030" data-mp="9iwts1zceirp">
          <div className="max-w-2xl" data-id="element-2031" data-mp="4kv95cm9qbe4">
            <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-2032" data-mp="4hpj9v5tf5hc">Showroom Items</p>
            <h1 id="inquiry-h1" className="mt-2 font-display text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-2033" data-mp="29fe2p0ah7i8j">
              Ask Us About It in the Showroom
            </h1>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-2034" data-mp="2ob3mcrs6vja3">
              We no longer list {subject} in our online shop. Selection changes through the year,
              so give us a call or send a text and we&rsquo;ll let you know what we have in Brooklyn
              right now.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2035" data-mp="323088e39kwy1">
              <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2036" data-mp="370sbu5d9u7i2">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-2037" data-mp="1gwzvxnkkmzpe" />
                Call {business.phone}
              </a>
              <a href={business.smsHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-2038" data-mp="1byojr66t0d40">
                <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-2039" data-mp="1zmeu9a8vhaq3" />
                Send a Text
              </a>
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-2040" data-mp="1unv5z36t3muk">
                <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-2041" data-mp="eaerhiwtl3q3" />
                Visit the Showroom
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Showroom details" className="w-full bg-white" data-id="element-2042" data-mp="54ovzhmwduz">
        <div className="mx-auto grid max-w-container grid-cols-1 gap-6 px-5 py-10 sm:grid-cols-3 sm:px-8" data-id="element-2043" data-mp="4552xj0tmn1i">
          <div className="flex items-start gap-2.5" data-id="element-2044" data-mp="3mjeci6dqphup">
            <MapPinIcon className="mt-1 h-5 w-5 shrink-0 text-pegasus-blue" aria-hidden="true" data-id="element-2045" data-mp="3rjafc2apb38o" />
            <address className="text-[16px] not-italic leading-6 text-ink" data-id="element-2046" data-mp="3clzvo5nwmr1v">
              {business.street}
              <br data-id="element-2047" data-mp="3hkuq64v8i41t" />
              {business.cityStateZip}
            </address>
          </div>
          <div className="flex items-start gap-2.5" data-id="element-2048" data-mp="33lbkdipwb4so">
            <ClockIcon className="mt-1 h-5 w-5 shrink-0 text-pegasus-blue" aria-hidden="true" data-id="element-2049" data-mp="33mwbszgwy7mb" />
            <div className="text-[16px] leading-6 text-ink" data-id="element-2050" data-mp="38ky6yirm5xe5">
              {business.hours.map(entry => <div key={entry.days} data-id="element-2051" data-mp="11uq9w15sgolb">
                  {entry.days} {entry.time}
                </div>)}
            </div>
          </div>
          <div data-id="element-2052" data-mp="32wpt388ug3rl">
            <SiteLink to={routes.supplies} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2053" data-mp="32v50ho63auwd">
              Browse supplies &amp; chemicals
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2054" data-mp="2jxln68clmq45" />
            </SiteLink>
          </div>
        </div>
      </section>
    </main>;
}